import { useEffect } from 'react';
import { WidgetWrapper } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { useTranslate } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { useUser } from '@clerk/clerk-react';

interface Props {
  readonly title?: string;
  /** Fallback text shown when `title` key is missing remotely (Tolgee default value). */
  readonly titleDefault?: string;
  readonly description?: string;
  /** Fallback text shown when `description` key is missing remotely. */
  readonly descriptionDefault?: string;
  readonly portalId?: string;
  readonly formId?: string;
  readonly guestFormId?: string;
  readonly link?: string;
  /** Appointment booking target shown after a successful submit (IM-14). */
  readonly bookingUrl?: string | null;
}

declare global {
  interface Window {
    // TODO: Consider making this global
    formInitialized?: boolean;
    readonly hbspt?: any;
    readonly agentselly?: {
      readonly loggedInUser?: {
        readonly contactId?: string;
        readonly email?: string;
      };
    };
  }
}

export const escapeHtml = (v: string): string =>
  v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export function buildHubspotSuccessHtml(
  successText: string,
  bookText: string,
  bookingUrl?: string | null,
): string {
  if (!bookingUrl) {
    return `<p>${escapeHtml(successText)}</p>`;
  }
  const isExternal = /^https?:\/\//.test(bookingUrl);
  const targetAttrs = isExternal ? ' target="_blank" rel="noreferrer"' : '';
  return `<p>${escapeHtml(successText)}</p><p><a href="${escapeHtml(
    bookingUrl,
  )}"${targetAttrs}>${escapeHtml(bookText)}</a></p>`;
}

export function HubspotFormWidget(props: Props) {
  const { t } = useTranslate();

  const { user } = useUser();

  const loggedInUserEmail =
    user?.primaryEmailAddress || window.agentselly?.loggedInUser?.email || '';

  const successHtml = buildHubspotSuccessHtml(
    t(
      'contact_form_success',
      'Sie werden innerhalb der nächsten Woche kontaktiert.',
    ),
    t('contact_form_success_book', 'Lieber gleich einen Termin vereinbaren'),
    props.bookingUrl,
  );

  const hubspotFormContainerId = `hubspotFormContainer_${props.formId}`;

  useEffect(() => {
    const loadHubspotFormScript = () => {
      if (
        !document.querySelector(
          'script[src="//js-eu1.hsforms.net/forms/embed/v2.js"]',
        )
      ) {
        const script = document.createElement('script');
        script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
        script.type = 'text/javascript';
        script.onload = () => {
          window.formInitialized = true;
          createHubspotForm();
        };
        document.body.appendChild(script);
      } else if (window.hbspt) {
        createHubspotForm();
      }
    };

    const createHubspotForm = () => {
      window.hbspt?.forms?.create({
        region: 'eu1',
        portalId: props.portalId,
        formId: loggedInUserEmail
          ? props.formId
          : props.guestFormId || props.formId,
        target: `#${hubspotFormContainerId}`,
        // Force HubSpot to render the form as raw inline HTML instead of inside
        // an iframe. By default the embed drops the form into a `.hs-form-iframe`
        // whose white theme our page CSS cannot cross — so the card stayed white
        // in dark mode. Passing css/cssRequired (even empty) opts out of the
        // iframe, letting the `.hs-form-embed` dark rules in main.css apply.
        // IM-12: fixed expectation message, IM-14: booking offer after submit.
        inlineMessage: successHtml,
        css: '',
        cssRequired: '',
        onFormReady: function (form: HTMLFormElement) {
          console.log('Hubspot form ready', props.portalId, props.formId);

          if (!loggedInUserEmail) {
            console.log(
              'No action needed since the guest form should be visible',
            );
            return;
          }

          const emailInput: any = form.querySelector('input[name="email"]');

          if (!emailInput) {
            console.error('Email input not found in form', form);
            return;
          }

          if (emailInput.value === loggedInUserEmail) {
            return;
          }

          if (emailInput.value) {
            console.warn(
              'Email input is not empty, but the logged in user email is different',
              emailInput.value,
              loggedInUserEmail,
            );
          }

          emailInput.value = loggedInUserEmail;
        },
      });
    };

    if (!window.formInitialized) {
      loadHubspotFormScript();
    } else if (window.hbspt) {
      createHubspotForm();
    }
  }, [
    successHtml,
    loggedInUserEmail,
    props.guestFormId,
    props.portalId,
    props.formId,
    hubspotFormContainerId,
  ]);

  return (
    <WidgetWrapper>
      <div className="flex flex-col gap-y-6 overflow-hidden">
        {props.title && (
          <h2 className="text-lg font-bold">
            {t(props.title, props.titleDefault)}
          </h2>
        )}
        {props.description && (
          <p>{t(props.description, props.descriptionDefault)}</p>
        )}
        <div
          id={hubspotFormContainerId}
          className="hs-form-embed"
        ></div>
      </div>

      {props.link ? (
        <AppLink
          to={props.link}
          className="self-start pt-4"
        >
          <Button showArrow>{t('widget_evaluation_button_text')}</Button>
        </AppLink>
      ) : null}
    </WidgetWrapper>
  );
}
